import React from 'react';
import {
  UserGroupIcon,
  UserPlusIcon,
  ClipboardDocumentListIcon,
  TableCellsIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

export const ContactManagementGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <UserGroupIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                Contact Management
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Learn how to create individual contacts, organize them into groups, and manage phone
              numbers so they&apos;re ready to use in Quick SMS and SMS Campaigns.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Overview steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Create a new contact
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use the <span className="font-medium">New Contact</span> page to add an individual
              contact linked to a group.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Create and manage groups
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use <span className="font-medium">My Groups</span> to define groups and maintain
              phone numbers in each group.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Part 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Import and export contacts
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use import/export tools to move contacts between CSV/Excel and the platform.
            </p>
          </div>
        </div>

        {/* New Contact section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <UserPlusIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                1. Create a new contact (New Contact page)
              </h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                From the navigation, open <span className="font-medium">New Contact</span> (the page
                built as <span className="font-mono">NewContactPage.tsx</span>).
              </li>
              <li>
                Fill in <span className="font-medium">First Name</span> and{' '}
                <span className="font-medium">Last Name</span>. These are saved to the backend
                `contacts` table as <span className="font-mono">first_name</span> and{' '}
                <span className="font-mono">last_name</span>.
              </li>
              <li>
                Add an <span className="font-medium">Email</span> address (optional but useful for
                reference and deduplication).
              </li>
              <li>
                Enter the <span className="font-medium">Phone</span> number. For consistency with SMS
                sending and validation rules, use the platform format{' '}
                <span className="font-mono">263712345678</span> where possible.
              </li>
              <li>
                Under <span className="font-medium">Group</span>:
                <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                  <li>
                    Choose an existing group from the dropdown (values loaded from the backend via{' '}
                    <span className="font-mono">GET /api/groups</span>).
                  </li>
                  <li>
                    Or select <span className="font-medium">Other</span> to enter a custom group
                    name. This is saved as the contact&apos;s <span className="font-mono">group</span>{' '}
                    field.
                  </li>
                </ul>
              </li>
              <li>
                Optionally add <span className="font-medium">Notes</span> for internal reference (for
                example: &quot;VIP customer&quot; or &quot;Prefers evening messages&quot;).
              </li>
              <li>
                Click <span className="font-medium">Add contact</span>. The frontend calls the
                backend <span className="font-mono">POST /api/contacts</span> with your data. On
                success, you&apos;ll see a green confirmation message.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              New contacts are stored in the `contacts` table and are available to use in Quick SMS,
              SMS Campaigns, and group management.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ClipboardDocumentListIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                2. Understand contact storage & backend behavior
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Behind the scenes, contacts are stored and retrieved using the API and MySQL tables
              described in the README and database notes.
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
              <li>
                Each contact is saved with fields like{' '}
                <span className="font-mono">first_name</span>,{' '}
                <span className="font-mono">last_name</span>,{' '}
                <span className="font-mono">email</span>,{' '}
                <span className="font-mono">phone</span>,{' '}
                <span className="font-mono">group</span> and{' '}
                <span className="font-mono">notes</span>.
              </li>
              <li>
                The <span className="font-medium">Group</span> dropdown is populated by calling{' '}
                <span className="font-mono">GET /api/groups</span>. This ensures that groups created
                in <span className="font-medium">My Groups</span> are available when you add new
                contacts.
              </li>
              <li>
                The New Contact list (on the contacts list page) uses pagination and group filters to
                help you find contacts quickly.
              </li>
              <li>
                The same phone‑number conventions used for SMS sending (Zimbabwe prefix{' '}
                <span className="font-mono">263</span>, 12 digits) should be followed when capturing
                and importing contacts so they can be used directly in campaigns.
              </li>
            </ul>
          </div>
        </div>

        {/* Groups creation & management */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <UserGroupIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              3. Create and manage groups (My Groups page)
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            The <span className="font-medium">My Groups</span> page (
            <span className="font-mono">MyGroupsPage.tsx</span>) lets you define named groups,
            attach phone numbers, and keep them in sync with contacts for campaigns.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              Open <span className="font-medium">My Groups</span> from the navigation. You&apos;ll
              see a &quot;Create new group&quot; form at the top and a Groups List below.
            </li>
            <li>
              In the <span className="font-medium">Create group</span> form:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  Enter a <span className="font-medium">Group name</span> (for example: &quot;VIP
                  Customers&quot; or &quot;Harare Staff&quot;).
                </li>
                <li>
                  Choose a <span className="font-medium">Group Type</span> such as Marketing,
                  Customer Service, Internal, or Other (with a custom label).
                </li>
                <li>
                  Optionally provide a <span className="font-medium">Description</span> to explain
                  how the group is used.
                </li>
              </ul>
            </li>
            <li>
              Under <span className="font-medium">Add phone numbers to group</span>, add one or more
              phone numbers:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>Enter numbers in the same format used for SMS (e.g. 263712345678).</li>
                <li>
                  The page validates that each number starts with <span className="font-mono">263</span>{' '}
                  and has exactly 12 digits, cleans out spaces/dashes, and rejects duplicates.
                </li>
                <li>
                  The <span className="font-medium">Phone Numbers</span> box shows up to 5 numbers
                  and the total count, with a hint if more are present.
                </li>
              </ul>
            </li>
            <li>
              Optionally click <span className="font-medium">Import phone numbers from CSV/Excel</span>{' '}
              to bulk‑add numbers using the import modal. The modal uses the same backend‑aligned
              rules as other import flows: CSV/Excel parsing via `xlsx`, validation and deduplication.
            </li>
            <li>
              Click <span className="font-medium">Create group</span>. The frontend:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  Calls <span className="font-mono">POST /api/groups</span> to create the group in
                  the `groups` table.
                </li>
                <li>
                  Iterates over the phone numbers to create or update matching contacts via{' '}
                  <span className="font-mono">createContact</span>/<span className="font-mono">
                    updateContact
                  </span>
                  , assigning them to this group.
                </li>
                <li>
                  Shows a summary of which phone numbers were saved successfully and which failed,
                  including backend error messages where available.
                </li>
              </ul>
            </li>
            <li>
              Use the <span className="font-medium">Groups List</span> table to:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  Search for groups, see <span className="font-medium">phone number counts</span>{' '}
                  and created dates.
                </li>
                <li>
                  Click the phone count to <span className="font-medium">view and manage phone numbers</span>{' '}
                  in that group (add/remove numbers using the same 263‑format validation).
                </li>
                <li>
                  Edit group details (name, type, description) or delete a group with confirmation.
                </li>
                <li>
                  Export a group to CSV using the <span className="font-medium">Download CSV</span>{' '}
                  action, which pulls data from the `contacts` table via the contacts API.
                </li>
              </ul>
            </li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            Groups and their phone numbers are used directly by the SMS Campaign audience selectors
            and contact import flows, so keeping them accurate ensures reliable messaging.
          </p>
        </div>

        {/* Import / export and best practices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TableCellsIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                4. Import contacts and phone numbers from files
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Use the platform&apos;s import tools whenever you need to bring in many contacts at
              once.
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
              <li>
                Prepare a CSV or Excel file with columns for <span className="font-medium">phone</span>{' '}
                and, optionally, <span className="font-medium">first name</span>,{' '}
                <span className="font-medium">last name</span>, <span className="font-medium">email</span>{' '}
                and <span className="font-medium">group</span>.
              </li>
              <li>
                Format the phone column as <span className="font-medium">Text</span> in Excel to
                avoid losing leading digits.
              </li>
              <li>
                Use the import actions on <span className="font-medium">New Contact</span> /{' '}
                <span className="font-medium">contacts</span> or{' '}
                <span className="font-medium">My Groups</span> (for group phone numbers). Imports are
                backed by <span className="font-mono">POST /api/contacts/import</span> and related
                endpoints.
              </li>
              <li>
                The preview shows up to 5 phone numbers plus the total imported count; any invalid
                numbers are skipped with a message.
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <PhoneIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                5. Best practices for clean contact data
              </h3>
            </div>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
              <li>
                Always store phone numbers in the full international format{' '}
                <span className="font-mono">263XXXXXXXXX</span> with no spaces or dashes.
              </li>
              <li>
                Use consistent group names (for example, &quot;Customers&quot; vs &quot;Customer&quot;)
                so filters and group lookups work correctly across the app.
              </li>
              <li>
                Periodically export groups or contacts to CSV and review them for duplicates or stale
                entries.
              </li>
              <li>
                When adding numbers from My Groups, rely on the built‑in validation and summary views
                to catch mistakes before using groups in SMS Campaigns.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactManagementGuidePage;


